import React, { useState, useEffect } from 'react';
import {
  FileSpreadsheet,
  Plus,
  Search,
  Filter,
  Building2,
  Calendar,
  Users,
  MapPin,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Hammer,
  Zap,
  Camera,
  CheckCheck,
  ChevronRight,
  ArrowRight,
  Shield,
  Smartphone,
  AlertCircle,
} from 'lucide-react';
import { WorkOrderItem, TicketItem, BranchItem, StaffItem, WorkflowType, HERE4U_WORK_TYPES } from '../types/index.ts';

interface WorkOrdersPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const WorkOrdersPage: React.FC<WorkOrdersPageProps> = ({ onNavigate }) => {
  const [workOrders, setWorkOrders] = useState<WorkOrderItem[]>([]);
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [staffList, setStaffList] = useState<StaffItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrderItem | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [form, setForm] = useState({
    ticketId: '',
    branchId: '1',
    workType: 'Paint / Tile / Seepage / Front Elevation',
    scope: '',
    supervisorName: 'Engr. Bilal Hashmi',
    teamName: 'Team Alpha',
    responsibleStaffId: '',
    assignedStaff: ['Kamran Ali (Senior Mason)'],
    requiredMaterials: '',
    requiredTools: '',
    expectedStartDate: new Date().toISOString().split('T')[0],
    expectedCompletionDate: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
    instructions: 'Follow banking safety guidelines. GPS check-in mandatory upon site arrival.',
    gpsRequired: true,
    workflowType: 'NORMAL' as WorkflowType,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [woRes, tktRes, bRes, stfRes] = await Promise.all([
        fetch('/api/work-orders').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/tickets').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/branches').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/staff').then((r) => (r.ok ? r.json() : [])),
      ]);

      if (Array.isArray(woRes)) {
        setWorkOrders(woRes);
        if (woRes.length > 0 && !selectedOrder) {
          setSelectedOrder(woRes[0]);
        }
      }
      if (Array.isArray(tktRes)) {
        setTickets(tktRes);
        if (tktRes.length > 0 && !form.ticketId) {
          setForm((prev) => ({ ...prev, ticketId: String(tktRes[0].id) }));
        }
      }
      if (Array.isArray(bRes)) setBranches(bRes);
      if (Array.isArray(stfRes)) setStaffList(stfRes);
    } catch (err: any) {
      console.warn('Work orders load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const res = await fetch('/api/work-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: Number(form.ticketId),
          branchId: Number(form.branchId),
          workType: form.workType,
          scope: form.scope,
          supervisorName: form.supervisorName,
          teamName: form.teamName,
          responsibleStaffId: form.responsibleStaffId ? Number(form.responsibleStaffId) : null,
          assignedStaff: form.assignedStaff,
          requiredMaterials: form.requiredMaterials,
          requiredTools: form.requiredTools,
          expectedStartDate: form.expectedStartDate,
          expectedCompletionDate: form.expectedCompletionDate,
          instructions: form.instructions,
          gpsRequired: form.gpsRequired,
          workflowType: form.workflowType,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Work Order created: ${data.workOrderNumber}`);
        setShowCreateModal(false);
        setForm({
          ticketId: tickets.length > 0 ? String(tickets[0].id) : '',
          branchId: '1',
          workType: 'Paint / Tile / Seepage / Front Elevation',
          scope: '',
          supervisorName: 'Engr. Bilal Hashmi',
          teamName: 'Team Alpha',
          responsibleStaffId: '',
          assignedStaff: ['Kamran Ali (Senior Mason)'],
          requiredMaterials: '',
          requiredTools: '',
          expectedStartDate: new Date().toISOString().split('T')[0],
          expectedCompletionDate: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
          instructions: 'Follow banking safety guidelines. GPS check-in mandatory upon site arrival.',
          gpsRequired: true,
          workflowType: 'NORMAL',
        });
        await fetchData();
      } else {
        setToastMessage(data.error || 'Failed to create work order');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Creation error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const filteredOrders = workOrders.filter((wo) => {
    const matchesStatus = filterStatus === 'ALL' || wo.status === filterStatus;
    const matchesSearch =
      wo.workOrderNumber.toLowerCase().includes(search.toLowerCase()) ||
      (wo.branchName && wo.branchName.toLowerCase().includes(search.toLowerCase())) ||
      (wo.branchCode && wo.branchCode.toLowerCase().includes(search.toLowerCase())) ||
      wo.scope.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Work Orders &amp; Site Dispatch
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900 border border-blue-200">
              Site Execution
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Authorize team mobilization, define scope of work, allocate materials and enforce mandatory GPS on-site attendance.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => onNavigate('attendance')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm transition-colors"
          >
            <MapPin className="w-3.5 h-3.5 text-blue-600" />
            Check-In Log
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-blue-200" />
            New Work Order
          </button>
        </div>
      </div>

      {toastMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-900 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Main Split Layout: Orders Table + Details Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[580px]">
        {/* Left Column: Work Order Table */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col shadow-xs">
          <div className="p-3.5 border-b border-slate-200 space-y-2.5 bg-slate-50/60">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1 overflow-x-auto text-[11px]">
                {(['ALL', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'VERIFIED'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setFilterStatus(mode)}
                    className={`px-2.5 py-1 rounded-md font-medium transition-colors whitespace-nowrap ${
                      filterStatus === mode
                        ? 'bg-blue-700 text-white'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {mode.replace('_', ' ')}
                  </button>
                ))}
              </div>

              <div className="relative w-44 sm:w-56 shrink-0">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter order, branch..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {filteredOrders.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                No work orders found matching the filter.
              </div>
            ) : (
              filteredOrders.map((wo) => {
                const isSelected = selectedOrder?.id === wo.id;
                return (
                  <div
                    key={wo.id}
                    onClick={() => setSelectedOrder(wo)}
                    className={`p-3.5 cursor-pointer transition-colors text-left ${
                      isSelected ? 'bg-blue-50/70 border-l-4 border-l-blue-600' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono font-bold text-slate-900">{wo.workOrderNumber}</span>
                        {wo.workflowType === 'MINOR_URGENT' && (
                          <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                            FAST TRACK
                          </span>
                        )}
                      </div>
                      <span className="text-slate-400">Target: {wo.expectedCompletionDate}</span>
                    </div>

                    <div className="text-xs font-semibold text-slate-900 truncate">
                      {wo.branchName || `Branch #${wo.branchId}`}
                    </div>

                    <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{wo.scope}</p>

                    <div className="flex items-center justify-between mt-2 text-[10px]">
                      <div className="text-slate-500">
                        Crew: <strong className="text-slate-700">{wo.teamName}</strong> ({wo.assignedStaff.length} tech)
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded font-semibold ${
                          wo.status === 'COMPLETED' || wo.status === 'VERIFIED'
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : wo.status === 'IN_PROGRESS'
                            ? 'bg-blue-50 text-blue-800 border border-blue-200'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {wo.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Order Details Inspector */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col shadow-xs">
          {selectedOrder ? (
            <div className="p-5 flex flex-col h-full space-y-4 text-xs">
              <div className="border-b border-slate-200 pb-3 flex items-start justify-between">
                <div>
                  <div className="text-[11px] font-mono text-slate-400">OFFICIAL WORK ORDER</div>
                  <h2 className="text-base font-bold text-slate-900 font-mono">{selectedOrder.workOrderNumber}</h2>
                  <div className="text-xs text-slate-600 mt-0.5">{selectedOrder.branchName}</div>
                </div>
                <span
                  className={`px-2.5 py-1 rounded text-xs font-bold ${
                    selectedOrder.workflowType === 'MINOR_URGENT'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-blue-50 text-blue-700 border border-blue-200'
                  }`}
                >
                  {selectedOrder.workflowType === 'MINOR_URGENT' ? 'Urgent Fast Track' : 'Normal BOQ Flow'}
                </span>
              </div>

              {/* Scope of Work */}
              <div className="space-y-1">
                <span className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Scope of Work</span>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 leading-relaxed">
                  {selectedOrder.scope}
                </div>
              </div>

              {/* Work Type & Specifications */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                  <span className="text-[10px] text-slate-400 uppercase block font-semibold">HERE4U Work Type</span>
                  <span className="font-semibold text-slate-800 text-[11px]">{selectedOrder.workType}</span>
                </div>
                <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                  <span className="text-[10px] text-slate-400 uppercase block font-semibold">Mandatory GPS Check-in</span>
                  <span className="font-semibold text-emerald-700 text-[11px]">
                    {selectedOrder.gpsRequired ? 'Required (Within 150m)' : 'Optional'}
                  </span>
                </div>
              </div>

              {/* Crew & Supervisor */}
              <div className="space-y-1.5">
                <span className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">Assigned Crew</span>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1 text-slate-700">
                  <div>
                    <span className="text-slate-400">Supervisor: </span>
                    <strong className="text-slate-900">{selectedOrder.supervisorName}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400">Team: </span>
                    <span>{selectedOrder.teamName}</span>
                  </div>
                  <div className="pt-1 text-[11px] text-slate-500">
                    Crew: {selectedOrder.assignedStaff.join(', ')}
                  </div>
                </div>
              </div>

              {/* Materials & Tools */}
              {(selectedOrder.requiredMaterials || selectedOrder.requiredTools) && (
                <div className="space-y-1.5">
                  <span className="font-bold text-slate-700 uppercase text-[10px] tracking-wider">
                    Materials &amp; Equipment
                  </span>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1 text-[11px] text-slate-700">
                    {selectedOrder.requiredMaterials && (
                      <div>
                        <strong className="text-slate-900">Materials: </strong>
                        {selectedOrder.requiredMaterials}
                      </div>
                    )}
                    {selectedOrder.requiredTools && (
                      <div>
                        <strong className="text-slate-900">Tools: </strong>
                        {selectedOrder.requiredTools}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Action Buttons */}
              <div className="pt-3 border-t border-slate-200 grid grid-cols-3 gap-1.5 mt-auto">
                <button
                  onClick={() =>
                    onNavigate(
                      'mobile-job',
                      selectedOrder.responsibleStaffId ? String(selectedOrder.responsibleStaffId) : undefined
                    )
                  }
                  className="px-2 py-2 text-xs font-bold rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 transition-colors inline-flex items-center justify-center gap-1 shadow-xs"
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  Field App
                </button>
                <button
                  onClick={() => onNavigate('attendance')}
                  className="px-2 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors inline-flex items-center justify-center gap-1"
                >
                  <MapPin className="w-3.5 h-3.5 text-blue-600" />
                  GPS
                </button>
                <button
                  onClick={() => onNavigate('evidence')}
                  className="px-2 py-2 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors inline-flex items-center justify-center gap-1"
                >
                  <Camera className="w-3.5 h-3.5" />
                  Photos
                </button>
              </div>
            </div>
          ) : (
            <div className="m-auto p-12 text-center text-slate-400">
              <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-xs">Select a work order from the list to view specifications.</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Work Order Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <Hammer className="w-4 h-4 text-blue-700" />
                <h3 className="text-sm font-bold text-slate-900">Issue New Execution Work Order</h3>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateOrder} className="p-5 space-y-3.5 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Target Ticket</label>
                  <select
                    required
                    value={form.ticketId}
                    onChange={(e) => {
                      const tkt = tickets.find((t) => t.id === Number(e.target.value));
                      setForm({
                        ...form,
                        ticketId: e.target.value,
                        branchId: tkt ? String(tkt.branchId) : form.branchId,
                        scope: tkt ? tkt.subject : form.scope,
                      });
                    }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  >
                    {tickets.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.ticketNumber} — {t.branchName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Workflow Speed</label>
                  <select
                    value={form.workflowType}
                    onChange={(e) => setForm({ ...form, workflowType: e.target.value as WorkflowType })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="NORMAL">Normal Flow (Survey &rarr; BOQ &rarr; Approval &rarr; Work)</option>
                    <option value="MINOR_URGENT">Minor / Urgent (Fast-Track Immediate Execution)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Work Type (9 HERE4U Types)</label>
                <select
                  value={form.workType}
                  onChange={(e) => setForm({ ...form, workType: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  {HERE4U_WORK_TYPES.map((wt) => (
                    <option key={wt} value={wt}>
                      {wt}
                    </option>
                  ))}
                </select>
              </div>

              {/* SKILLS-MATCHED QUALIFIED TECHNICIAN SELECTOR */}
              <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-slate-800 font-bold text-[11px] uppercase tracking-wider text-blue-900">
                    Assigned Responsible Technician (Skills-Matched)
                  </label>
                  <span className="text-[10px] text-blue-700 font-semibold">
                    {
                      staffList.filter(
                        (s) =>
                          (Array.isArray(s.workTypes) &&
                            s.workTypes.some((w) => w.toLowerCase().includes(form.workType.toLowerCase()))) ||
                          s.skill.toLowerCase().includes(form.workType.toLowerCase())
                      ).length
                    }{' '}
                    Qualified Technicians
                  </span>
                </div>

                <select
                  value={form.responsibleStaffId}
                  onChange={(e) => {
                    const stf = staffList.find((s) => s.id === Number(e.target.value));
                    setForm({
                      ...form,
                      responsibleStaffId: e.target.value,
                      assignedStaff: stf ? [stf.name] : form.assignedStaff,
                      teamName: stf ? stf.teamName : form.teamName,
                    });
                  }}
                  className="w-full px-3 py-2 bg-white border border-blue-300 rounded-lg text-xs font-semibold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">Select Technician for {form.workType}...</option>
                  {staffList.map((stf) => {
                    const assignedMatch = stf.assignedSkills?.find((sk) => {
                      const name = (sk.skillName || '').toLowerCase();
                      const cat = (sk.category || '').toLowerCase();
                      const target = form.workType.toLowerCase();
                      return name.includes(target) || target.includes(name) || (cat && target.includes(cat));
                    });

                    const isVerified = assignedMatch?.verificationStatus === 'VERIFIED';
                    const isUnverified = assignedMatch && assignedMatch.verificationStatus !== 'VERIFIED';
                    const isGenericQualified =
                      !assignedMatch &&
                      ((Array.isArray(stf.workTypes) &&
                        stf.workTypes.some((w) => w.toLowerCase().includes(form.workType.toLowerCase()))) ||
                        stf.skill.toLowerCase().includes(form.workType.toLowerCase()));

                    let prefix = '';
                    let tag = '';
                    if (isVerified) {
                      prefix = '★ ';
                      tag = ` [VERIFIED - ${assignedMatch.proficiency}]`;
                    } else if (isUnverified) {
                      prefix = '⚠️ ';
                      tag = ` [UNVERIFIED - ${assignedMatch.proficiency}]`;
                    } else if (isGenericQualified) {
                      prefix = '★ ';
                      tag = ' [QUALIFIED]';
                    }

                    return (
                      <option key={stf.id} value={stf.id}>
                        {prefix}
                        {stf.name} ({stf.employeeId || `EMP-${stf.id}`} - {stf.designation})
                        {tag}
                      </option>
                    );
                  })}
                </select>

                {/* Verification Warning Notice if unverified technician is chosen */}
                {form.responsibleStaffId && (() => {
                  const stf = staffList.find((s) => s.id === Number(form.responsibleStaffId));
                  const verifiedSkill = stf?.assignedSkills?.find((sk) => {
                    const name = (sk.skillName || '').toLowerCase();
                    const cat = (sk.category || '').toLowerCase();
                    const target = form.workType.toLowerCase();
                    return (
                      (name.includes(target) || target.includes(name) || (cat && target.includes(cat))) &&
                      sk.verificationStatus === 'VERIFIED'
                    );
                  });
                  if (stf && !verifiedSkill) {
                    return (
                      <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-900 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-bold">Safety &amp; Compliance Warning: </span>
                          <span>
                            {stf.name} does not hold a supervisor-verified qualification for &quot;{form.workType}&quot;.
                            On-site work execution requires supervisor oversight and subsequent qualification verification.
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <p className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Technician holds verified qualification for {form.workType}. Authorized for autonomous site execution.
                    </p>
                  );
                })()}
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Detailed Scope of Work</label>
                <textarea
                  rows={2}
                  required
                  placeholder="Detail the exact repair, dimensions, or installations required on site..."
                  value={form.scope}
                  onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Assigned Team</label>
                  <select
                    value={form.teamName}
                    onChange={(e) => setForm({ ...form, teamName: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="South Rapid Response">South Rapid Response</option>
                    <option value="Team Alpha">Team Alpha</option>
                    <option value="Team Beta">Team Beta</option>
                    <option value="Punjab Field Force">Punjab Field Force</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Supervisor</label>
                  <input
                    type="text"
                    required
                    value={form.supervisorName}
                    onChange={(e) => setForm({ ...form, supervisorName: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Required Materials</label>
                  <input
                    type="text"
                    placeholder="e.g. Sika chemical, tile bond, gas cylinder"
                    value={form.requiredMaterials}
                    onChange={(e) => setForm({ ...form, requiredMaterials: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Required Tools</label>
                  <input
                    type="text"
                    placeholder="e.g. Tile cutter, drill, manifold gauge"
                    value={form.requiredTools}
                    onChange={(e) => setForm({ ...form, requiredTools: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Expected Start</label>
                  <input
                    type="date"
                    required
                    value={form.expectedStartDate}
                    onChange={(e) => setForm({ ...form, expectedStartDate: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Target Completion</label>
                  <input
                    type="date"
                    required
                    value={form.expectedCompletionDate}
                    onChange={(e) => setForm({ ...form, expectedCompletionDate: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Special Site Instructions</label>
                <input
                  type="text"
                  value={form.instructions}
                  onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                <input
                  type="checkbox"
                  checked={form.gpsRequired}
                  onChange={(e) => setForm({ ...form, gpsRequired: e.target.checked })}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[11px] font-semibold">
                  Enforce Mandatory GPS Check-in (Technicians must check-in within 150m radius of branch)
                </span>
              </label>

              <div className="pt-2 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Generating...' : 'Issue Work Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
